/**
 * Canvas asset (image/video) load-timing instrumentation (RFC-0974 app-health
 * follow-up, §5.5).
 *
 * Emits exactly one `canvas_asset_load_finished` per source-assignment attempt:
 *   start   = a non-empty `src` is assigned AND the element is on-screen (or the
 *             asset is already cached) — offscreen/background preloads are
 *             excluded (§5.5) via an IntersectionObserver gate
 *   ready   = the element decodes / becomes render-ready (img.onLoad /
 *             video.onLoadedData, or already-complete on attach — cached)
 *   error   = the element fails to load (img/video onError)
 *   timeout = not ready within 15s of the attempt starting
 *
 * Lifecycle ownership (concurrent-safe + ordering-safe):
 *  - A `useLayoutEffect` keyed on `src` is the SOLE owner of the attempt: it runs
 *    ONLY for committed renders (so a discarded/aborted concurrent render never
 *    touches a live attempt — no render-phase side effects) AND synchronously
 *    after commit, BEFORE the browser dispatches the new source's async
 *    load/error events. So `attemptRef` never lags `src`: a terminal is always
 *    attributed to the correct source.
 *  - `mediaRef` only STORES the element; it arms solely for the late-mount case
 *    (the layout effect already claimed this src but had no element yet), so a
 *    normal mount never double-emits a cached asset.
 *  - `trigger` is snapshotted WHEN THE ATTEMPT STARTS (reading the hydration
 *    latch then), never at terminal time.
 *  - Pre-attempt terminal buffering: a ready/error that fires before the attempt
 *    starts is buffered (tagged with its source) and consumed only if it matches.
 *  - Remount dedupe: once a node's source RESOLVES (ready/error), a later remount
 *    of that same source is not re-timed. Keyed by `${projectId}:${nodeId}` (no
 *    cross-project collision); recorded on COMPLETION so a cancelled attempt can
 *    still retry.
 *  - The returned handlers COMPOSE with the caller's existing onLoad/onError.
 *  - project_id is REQUIRED (§5.5) — absent ⇒ the terminal is skipped, never an
 *    empty identity.
 *  - Best-effort: only PRIMARY on-canvas displays should use this hook.
 *  - RFC-1577: optional `mediaType` (the node OUTPUT's media type — a video
 *    node's poster is `asset_kind: image`, `media_type: video`) and `variantId`
 *    (the picked variant, when the caller knows it). Audio is not covered: the
 *    canvas cards render no audio element at idle, so there is no comparable
 *    ready/error signal to time.
 */

import { useCallback, useLayoutEffect, useRef } from 'react'
import { useOptionalProjectId } from '../contexts/ProjectIdContext'
import { useCanvasHydrated } from '../contexts/CanvasHydrationContext'
import { createNadDebug } from '../utils/debugLog'
import { trackCanvasAssetLoadFinished, type AssetKind, type AssetTrigger } from './events'
import type { OutputMediaType } from './generated/analyticsEvents'

const log = createNadDebug('dw:analytics-events:asset-load')

const TIMEOUT_MS = 15_000
const VIDEO_HAVE_CURRENT_DATA = 2

// Last-RESOLVED source per `${projectId}:${nodeId}` (module-scoped so it survives
// a remount). Bounded so a long session can't grow it unboundedly. Nodes without
// an id do not participate.
const resolvedSrcByNode = new Map<string, string>()
const MAX_TRACKED_NODES = 512

type LoadableElement = HTMLImageElement | HTMLVideoElement | null
type TerminalOutcome = 'ready' | 'error' | 'timeout'

interface Attempt {
  src: string
  startedAt: number
  trigger: AssetTrigger
  done: boolean
  // All attribution metadata is SNAPSHOTTED into the attempt when it begins (at a
  // committed moment), so a terminal never reads a live/speculative ref value.
  projectId: string | undefined
  nodeId: string | undefined
  assetKind: AssetKind
  mediaType: OutputMediaType | undefined
  variantId: string | undefined
}

export interface CanvasAssetLoadHandlers {
  /** Attach to `<img ref>` / `<video ref>` — stores the element (+ late-mount arm). */
  mediaRef: (el: LoadableElement) => void
  /** Attach to `onLoad` (img) / `onLoadedData` (video). */
  onReady: () => void
  /** Attach to `onError`. */
  onError: () => void
}

function elementIsReady(el: LoadableElement): boolean {
  if (!el) return false
  if (el instanceof HTMLImageElement) return el.complete && el.naturalWidth > 0
  return el.readyState >= VIDEO_HAVE_CURRENT_DATA // HTMLVideoElement
}

/**
 * Instrument one canvas asset element. `src` is the currently-assigned source
 * (empty/undefined ⇒ no attempt). `nodeId` is optional debugging context.
 */
export function useCanvasAssetLoad({
  assetKind,
  src,
  nodeId,
  mediaType,
  variantId,
}: {
  assetKind: AssetKind
  src: string | null | undefined
  nodeId?: string
  /**
   * RFC-1577: media type of the node's OUTPUT (a video node's poster is
   * `asset_kind: image` but `media_type: video`). Optional.
   */
  mediaType?: OutputMediaType
  /** RFC-1577: `${nodeId}:${pickedIndex}` when the caller knows the picked variant. */
  variantId?: string
}): CanvasAssetLoadHandlers {
  const projectId = useOptionalProjectId()
  const hydrated = useCanvasHydrated()

  const attemptRef = useRef<Attempt | null>(null)
  const elementRef = useRef<LoadableElement>(null)
  const timerRef = useRef<number | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  // The source the layout effect has already claimed (armed / reset for).
  // The COMMITTED source — mutated ONLY inside the useLayoutEffect below (which
  // runs solely for committed renders). All attempt/terminal correctness reads
  // this, NEVER the render-updated `srcRef`, so a speculative/aborted concurrent
  // render can never cause a terminal to be dropped or mis-attributed.
  const armedSrcRef = useRef<string | null | undefined>(undefined)
  // A ready/error that fired before the attempt started — tagged with the source
  // it was captured for, so a stale-source terminal is never mis-attributed.
  const pendingOutcomeRef = useRef<{ outcome: 'ready' | 'error'; src: string } | null>(null)
  // NOTE: there is intentionally NO render-mutated `srcRef`. All correctness reads
  // the committed `armedSrcRef` (mutated only in the layout effect); `mediaRef`
  // captures `src` in its closure (ref callbacks run only for committed renders).
  const assetKindRef = useRef<AssetKind>(assetKind)
  const nodeIdRef = useRef<string | undefined>(nodeId)
  const mediaTypeRef = useRef<OutputMediaType | undefined>(mediaType)
  const variantIdRef = useRef<string | undefined>(variantId)
  const hydratedRef = useRef(hydrated)
  const projectIdRef = useRef(projectId)
  assetKindRef.current = assetKind
  nodeIdRef.current = nodeId
  mediaTypeRef.current = mediaType
  variantIdRef.current = variantId
  hydratedRef.current = hydrated
  projectIdRef.current = projectId

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const disconnectObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
  }, [])

  const emitTerminal = useCallback(
    (outcome: TerminalOutcome) => {
      const committedSrc = armedSrcRef.current
      const attempt = attemptRef.current
      if (!attempt) {
        // Terminal arrived before the attempt started — buffer ready/error so it
        // is reported once the attempt begins (never lost / turned into timeout).
        if ((outcome === 'ready' || outcome === 'error') && committedSrc) {
          pendingOutcomeRef.current = { outcome, src: committedSrc }
        }
        return
      }
      if (attempt.done) return
      // Ignore a terminal for a STALE source (defense — the layout effect already
      // keeps attemptRef current, so this should not normally trigger).
      if (attempt.src !== committedSrc) return
      attempt.done = true
      clearTimer()
      // Attribution reads the attempt's SNAPSHOT, never live refs.
      const project_id = attempt.projectId
      if (!project_id) {
        log({ event: 'asset-load-skip-no-project', outcome, asset_kind: attempt.assetKind })
        return
      }
      // Remount dedupe: record ONLY a successful (`ready`) resolution — an `error`
      // or `timeout` is left un-recorded so a later remount/retry of the same URL
      // is re-timed (a transient failure that later succeeds must not be silently
      // suppressed). Bounded as a true LRU: refresh this key's position, then
      // evict the OLDEST key(s) when over cap — never clear() the whole map (which
      // would re-admit duplicates for every still-mounted node on a huge canvas).
      const nid = attempt.nodeId
      if (nid && outcome === 'ready') {
        const key = `${project_id}:${nid}`
        resolvedSrcByNode.delete(key)
        resolvedSrcByNode.set(key, attempt.src)
        while (resolvedSrcByNode.size > MAX_TRACKED_NODES) {
          const oldest = resolvedSrcByNode.keys().next().value
          if (oldest === undefined) break
          resolvedSrcByNode.delete(oldest)
        }
      }
      trackCanvasAssetLoadFinished({
        project_id,
        asset_kind: attempt.assetKind,
        trigger: attempt.trigger,
        duration_ms: Math.max(0, Math.round(Date.now() - attempt.startedAt)),
        outcome,
        node_id: nid,
        media_type: attempt.mediaType,
        variant_id: attempt.variantId,
      })
    },
    [clearTimer],
  )

  // Begin an attempt for the current src (snapshot trigger now, start the timer).
  const beginAttempt = useCallback(() => {
    const currentSrc = armedSrcRef.current
    if (!currentSrc || attemptRef.current) return
    const nid = nodeIdRef.current
    const pid = projectIdRef.current
    // Remount dedupe: this node+source already resolved ⇒ do not re-time. Refresh
    // its LRU recency on this cache HIT (delete+set) so a hot, frequently-remounted
    // asset isn't evicted as "oldest" on a very large canvas.
    if (nid && pid) {
      const key = `${pid}:${nid}`
      if (resolvedSrcByNode.get(key) === currentSrc) {
        resolvedSrcByNode.delete(key)
        resolvedSrcByNode.set(key, currentSrc)
        return
      }
    }
    // Snapshot ALL attribution metadata now (a committed moment: beginAttempt is
    // only reached from the layout effect or a commit-phase ref callback), so the
    // eventual terminal never reads a live/speculative ref.
    attemptRef.current = {
      src: currentSrc,
      startedAt: Date.now(),
      // Sources assigned after the canvas first became interactive are treated as
      // post-generation; those during the initial hydration window are hydration.
      trigger: hydratedRef.current ? 'post_generation' : 'canvas_hydration',
      done: false,
      projectId: pid,
      nodeId: nid,
      assetKind: assetKindRef.current,
      mediaType: mediaTypeRef.current,
      variantId: variantIdRef.current,
    }
    timerRef.current = window.setTimeout(() => emitTerminal('timeout'), TIMEOUT_MS)
    // Consume a terminal that fired before the attempt started — only if it was
    // captured for THIS source (a stale-source buffer is discarded).
    if (pendingOutcomeRef.current) {
      const buffered = pendingOutcomeRef.current
      pendingOutcomeRef.current = null
      if (buffered.src === currentSrc) {
        emitTerminal(buffered.outcome)
        return
      }
    }
    // Cached and already decoded ⇒ ready immediately (duration ~0).
    if (elementIsReady(elementRef.current)) emitTerminal('ready')
  }, [emitTerminal])

  // Arm: start now if cached/ready, else start once the element enters view.
  const arm = useCallback(
    (el: LoadableElement) => {
      if (!el || !armedSrcRef.current || attemptRef.current) return
      if (elementIsReady(el)) {
        beginAttempt()
        return
      }
      if (typeof IntersectionObserver === 'undefined') {
        // No observer (SSR) ⇒ treat as on-screen and begin immediately.
        beginAttempt()
        return
      }
      disconnectObserver()
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          disconnectObserver()
          beginAttempt()
        }
      })
      observer.observe(el)
      observerRef.current = observer
    },
    [beginAttempt, disconnectObserver],
  )

  // SOLE attempt owner: runs only for committed renders, synchronously after
  // commit and BEFORE the new source's async load/error events. Resets on a real
  // source change (never touching an aborted render), then arms.
  useLayoutEffect(() => {
    if (armedSrcRef.current !== src) {
      clearTimer()
      disconnectObserver()
      attemptRef.current = null
      pendingOutcomeRef.current = null
      armedSrcRef.current = src
    }
    if (src && !attemptRef.current) arm(elementRef.current)
    return () => {
      clearTimer()
      disconnectObserver()
    }
  }, [src, arm, clearTimer, disconnectObserver])

  const mediaRef = useCallback(
    (el: LoadableElement) => {
      elementRef.current = el
      // Late-mount only: the layout effect already claimed this src but had no
      // element then. `src` is captured in this closure (ref callbacks run only
      // for committed renders, so it is a committed value). A NORMAL mount/src
      // change leaves armedSrcRef !== src here (the layout effect has not run
      // yet), so this does NOT arm → no cached double-emit.
      if (el && armedSrcRef.current === src && !attemptRef.current) arm(el)
    },
    [arm, src],
  )

  const onReady = useCallback(() => emitTerminal('ready'), [emitTerminal])
  const onError = useCallback(() => emitTerminal('error'), [emitTerminal])

  return { mediaRef, onReady, onError }
}

/** Test helper — clear the module-scoped remount-dedupe map. */
export function _resetAssetLoadDedupeForTest(): void {
  resolvedSrcByNode.clear()
}
